/**
 * SubstituteItemModal — swap a line on a paid Bridge Club order.
 *
 * Money moves here, so two rules shape the whole component:
 *
 *  1. The figure staff sees is the figure the SERVER computed. The modal
 *     never does its own price arithmetic — it POSTs {preview: true} to the
 *     same endpoint that will perform the substitution and renders what comes
 *     back. A number on screen that disagreed with the cash in the drawer
 *     would be worse than no number at all.
 *
 *  2. Nothing is submitted until an explicit confirm step that states the
 *     amount and the direction in words ("Refund $9.72 cash to the customer").
 *
 * Styled with inline objects, not Tailwind — Tailwind utilities are not
 * applied in this build and fail silently.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

export interface SubstituteOrderLine {
  id: number;
  name: string;
  quantity: number;
}

export interface SubstituteOrder {
  id: number;
  order_number: string;
  customer_name: string;
  items: SubstituteOrderLine[];
}

interface StoreProductOption {
  id: number;
  name: string;
  effective_price: number;
  is_active?: boolean;
}

interface PreviewResult {
  original_name: string;
  original_line_total: number;
  replacement_name: string;
  replacement_line_total: number;
  quantity: number;
  charged_total: number;
  projected_total: number;
  delta: number;
  direction: 'refund' | 'owed' | 'none';
  amount: number;
  requires_money_resolution: boolean;
  has_open_session: boolean;
  open_pos_session_id: number | null;
}

interface Props {
  order: SubstituteOrder;
  onSuccess: (result: any) => void;
  onClose: () => void;
}

const SubstituteItemModal: React.FC<Props> = ({ order, onSuccess, onClose }) => {
  const [lineId, setLineId] = useState<number | null>(
    order.items.length === 1 ? order.items[0].id : null,
  );
  const [products, setProducts] = useState<StoreProductOption[]>([]);
  const [productId, setProductId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [resolution, setResolution] = useState<'waive' | 'collect' | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get('/store/products/')
      .then(r => {
        if (cancelled) return;
        const list: StoreProductOption[] = (r.data || [])
          .filter((p: any) => p.is_active !== false);
        setProducts(list);
      })
      .catch(() => setError('Could not load products.'));
    return () => { cancelled = true; };
  }, []);

  // Any change to the swap invalidates the priced preview — staff must never
  // confirm an amount computed for a different pair.
  const invalidate = () => {
    setPreview(null);
    setResolution(null);
    setConfirming(false);
  };

  const fetchPreview = useCallback(async () => {
    if (!lineId || !productId) return;
    setPreviewing(true);
    setError('');
    try {
      const r = await api.post(
        `/store/bc-orders/${order.id}/substitute-line/`,
        { line_id: lineId, replacement_product_id: productId, preview: true },
      );
      setPreview(r.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not price that swap.');
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, [order.id, lineId, productId]);

  useEffect(() => {
    if (lineId && productId) fetchPreview();
  }, [lineId, productId, fetchPreview]);

  const handleSubmit = async () => {
    if (!lineId || !productId || !preview) return;
    setSubmitting(true);
    setError('');
    try {
      const body: any = {
        line_id: lineId,
        replacement_product_id: productId,
      };
      if (preview.requires_money_resolution) body.money_resolution = resolution;
      const r = await api.post(
        `/store/bc-orders/${order.id}/substitute-line/`, body,
      );
      onSuccess(r.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Substitution failed.');
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const money = (n: number) => `$${Math.abs(n).toFixed(2)}`;

  const deltaSentence = (): string => {
    if (!preview) return '';
    if (preview.direction === 'refund') {
      return `Customer is owed ${money(preview.amount)}`;
    }
    if (preview.direction === 'owed') {
      return `Customer owes ${money(preview.amount)}`;
    }
    return 'No difference';
  };

  const canConfirm =
    !!preview &&
    (!preview.requires_money_resolution || resolution !== null) &&
    !(preview.direction === 'refund' && !preview.has_open_session);

  const visible = products.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={styles.overlay} onClick={() => !submitting && onClose()}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <header style={styles.header}>
          <h2 style={styles.title}>Substitute Item</h2>
          <button style={styles.close} onClick={onClose} disabled={submitting}>×</button>
        </header>

        <div style={styles.body}>
          <p style={styles.orderLine}>
            Order <strong>{order.order_number}</strong> · {order.customer_name}
          </p>

          {/* Step 1 — which line */}
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>1. Which item is out of stock?</h3>
            {order.items.map(it => (
              <button
                key={it.id}
                style={{
                  ...styles.pick,
                  ...(lineId === it.id ? styles.pickOn : {}),
                }}
                onClick={() => { setLineId(it.id); invalidate(); }}
                disabled={submitting}
              >
                {it.quantity}× {it.name}
              </button>
            ))}
          </section>

          {/* Step 2 — replacement */}
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>2. Substitute with</h3>
            <input
              style={styles.search}
              placeholder="Search products…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              disabled={submitting}
            />
            <div style={styles.productList}>
              {visible.slice(0, 40).map(p => (
                <button
                  key={p.id}
                  style={{
                    ...styles.pick,
                    ...(productId === p.id ? styles.pickOn : {}),
                  }}
                  onClick={() => { setProductId(p.id); invalidate(); }}
                  disabled={submitting}
                >
                  {p.name} — ${p.effective_price.toFixed(2)}
                </button>
              ))}
              {visible.length === 0 && (
                <p style={styles.muted}>No products match.</p>
              )}
            </div>
          </section>

          {/* Step 3 — the money, straight from the server */}
          {previewing && <p style={styles.muted}>Pricing…</p>}
          {preview && (
            <section style={styles.section}>
              <h3 style={styles.sectionTitle}>3. Price difference</h3>
              <div
                style={{
                  ...styles.deltaBox,
                  ...(preview.direction === 'refund' ? styles.deltaRefund
                      : preview.direction === 'owed' ? styles.deltaOwed
                      : styles.deltaNone),
                }}
              >
                <div style={styles.deltaAmount}>{deltaSentence()}</div>
                <div style={styles.deltaDetail}>
                  {preview.original_name} ({money(preview.original_line_total)})
                  {' → '}
                  {preview.replacement_name} ({money(preview.replacement_line_total)})
                </div>
                <div style={styles.deltaDetail}>
                  Charged {money(preview.charged_total)} · order becomes{' '}
                  {money(preview.projected_total)}
                </div>
              </div>

              {preview.direction === 'refund' && !preview.has_open_session && (
                <p style={styles.blocker}>
                  You do not have a POS session open, so a cash refund cannot
                  be paid out. Open a session on your register, then try again.
                </p>
              )}

              {preview.requires_money_resolution && (
                <div style={styles.resolutionRow}>
                  <button
                    style={{
                      ...styles.resBtn,
                      ...(resolution === 'waive' ? styles.resOn : {}),
                    }}
                    onClick={() => setResolution('waive')}
                    disabled={submitting}
                  >
                    Waive — store absorbs {money(preview.amount)}
                  </button>
                  <button
                    style={{
                      ...styles.resBtn,
                      ...(resolution === 'collect' ? styles.resOn : {}),
                    }}
                    onClick={() => setResolution('collect')}
                    disabled={submitting}
                  >
                    Collect {money(preview.amount)} cash
                  </button>
                </div>
              )}
            </section>
          )}

          {error && <div style={styles.error} role="alert">{error}</div>}

          {/* Explicit confirm — never move money without this */}
          {confirming && preview && (
            <div style={styles.confirmBox} role="alert">
              <strong>Confirm:</strong>{' '}
              {preview.direction === 'refund'
                && `Refund ${money(preview.amount)} cash to the customer.`}
              {preview.direction === 'owed' && resolution === 'collect'
                && `Collect ${money(preview.amount)} cash from the customer.`}
              {preview.direction === 'owed' && resolution === 'waive'
                && `Absorb ${money(preview.amount)} — customer pays nothing more.`}
              {preview.direction === 'none' && 'Swap the item. No money moves.'}
            </div>
          )}
        </div>

        <footer style={styles.footer}>
          <button style={styles.secondary} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          {!confirming ? (
            <button
              style={{ ...styles.primary, ...(canConfirm ? {} : styles.disabled) }}
              onClick={() => setConfirming(true)}
              disabled={!canConfirm || submitting}
            >
              Review
            </button>
          ) : (
            <button
              style={styles.primary}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Working…' : 'Confirm Substitution'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '1rem',
  },
  // Full-height on phones, centred card on desktop.
  modal: {
    backgroundColor: 'white', borderRadius: '10px', width: '100%',
    maxWidth: '520px', maxHeight: '90vh', display: 'flex',
    flexDirection: 'column', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb',
  },
  title: { margin: 0, fontSize: '1.1rem' },
  close: {
    background: 'none', border: 'none', fontSize: '1.6rem', lineHeight: 1,
    cursor: 'pointer', minHeight: '44px', minWidth: '44px', color: '#6b7280',
  },
  body: { padding: '1rem 1.25rem', overflowY: 'auto', flex: 1 },
  orderLine: { margin: '0 0 1rem', color: '#374151' },
  section: { marginBottom: '1.25rem' },
  sectionTitle: { fontSize: '0.9rem', margin: '0 0 0.5rem', color: '#111827' },
  search: {
    width: '100%', padding: '0.6rem', fontSize: '1rem',
    border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '0.5rem',
  },
  productList: { maxHeight: '190px', overflowY: 'auto' },
  pick: {
    display: 'block', width: '100%', textAlign: 'left', padding: '0.6rem 0.75rem',
    marginBottom: '0.35rem', minHeight: '44px', border: '1px solid #d1d5db',
    borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer',
    fontSize: '0.95rem',
  },
  pickOn: {
    borderColor: '#059669', backgroundColor: '#ecfdf5', fontWeight: 600,
  },
  deltaBox: { padding: '0.85rem', borderRadius: '8px', border: '1px solid' },
  deltaRefund: { backgroundColor: '#ecfdf5', borderColor: '#059669' },
  deltaOwed: { backgroundColor: '#fffbeb', borderColor: '#d97706' },
  deltaNone: { backgroundColor: '#f9fafb', borderColor: '#d1d5db' },
  deltaAmount: { fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.35rem' },
  deltaDetail: { fontSize: '0.85rem', color: '#4b5563' },
  resolutionRow: {
    display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem',
  },
  resBtn: {
    padding: '0.65rem', minHeight: '44px', border: '1px solid #d1d5db',
    borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer',
    fontSize: '0.95rem',
  },
  resOn: { borderColor: '#d97706', backgroundColor: '#fffbeb', fontWeight: 600 },
  blocker: {
    marginTop: '0.65rem', padding: '0.65rem', borderRadius: '6px',
    backgroundColor: '#fef2f2', color: '#991b1b', fontSize: '0.9rem',
  },
  confirmBox: {
    padding: '0.85rem', borderRadius: '8px', backgroundColor: '#eff6ff',
    border: '1px solid #2563eb', color: '#1e3a8a', fontSize: '0.95rem',
  },
  error: {
    padding: '0.65rem', borderRadius: '6px', backgroundColor: '#fef2f2',
    color: '#991b1b', fontSize: '0.9rem', marginBottom: '0.75rem',
  },
  footer: {
    display: 'flex', gap: '0.5rem', justifyContent: 'flex-end',
    padding: '0.85rem 1.25rem', borderTop: '1px solid #e5e7eb',
  },
  secondary: {
    padding: '0.6rem 1.1rem', minHeight: '44px', borderRadius: '6px',
    border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer',
  },
  primary: {
    padding: '0.6rem 1.1rem', minHeight: '44px', borderRadius: '6px',
    border: 'none', backgroundColor: '#059669', color: 'white',
    fontWeight: 600, cursor: 'pointer',
  },
  disabled: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
  muted: { color: '#6b7280', fontSize: '0.9rem' },
};

export default SubstituteItemModal;
